import React from 'react';

import {
  FormDropdown,
  FormFieldStack,
  FormInput,
  NumberInput,
  UnitNumberField,
  SubsectionForm,
} from '../../../../components/forms';
import { SelectedFile } from '../../../../components/forms/FileUpload';
import { requestStepFieldModeProps } from '../../../../components/forms/viewOnlyFieldUtils';
import { PermissionAwareField } from '../../../../components/PermissionAwareField';
import { FACILITY_POWER_DISTRIBUTION_SUBTITLE } from '../../../../constants/facilityPowerSectionSubtitles';
import { FACILITY_POWER_DISTRIBUTION_SUBSECTION_FIELDS } from '../../../../constants/requestFieldDescriptors';
import { SECTION_IDS } from '../../../../constants/sectionIds';
import { useFormFieldVisibilityContext } from '../../../../hooks/useFormFieldVisibilityContext';
import { SavedAttachment } from '../../../../services/attachmentService';
import { Request } from '../../../../types';
import { subsectionHeaderMetricsProps } from '../../../../utils/subsectionHeaderMetrics';

type PowerDistributionAttachmentBundle = {
  attachmentsBySection: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
};

export type PowerDistributionInformationProps = {
  sectionTitle?: string;
} & (
  | ({
      mode: 'initial';
      formData: Record<string, unknown>;
      updateFormData: (
        field: string,
        value: string | number | boolean | undefined,
      ) => void;
      requestId?: string;
      disabled: boolean;
      isEditing: boolean;
      showFullDetails?: boolean;
    } & PowerDistributionAttachmentBundle)
  | ({
      mode: 'persisted';
      request: Request;
      isEditing: boolean;
      editedData?: Record<string, unknown>;
      showFullDetails?: boolean;
      getValue: (field: string) => string | number | boolean;
      updateEditedData: (
        field: string,
        value: string | number | boolean | undefined,
      ) => void;
    } & PowerDistributionAttachmentBundle)
);

const distributionMethodOptions = [
  'Overhead busway',
  'Under floor cabling to rack',
];
const voltageOptions = ['208', '230', '240', '380', '400', '415', '480', '600'];

/**
 * Main “Power Distribution” card for create and opened-request layouts.
 * Stage gating is handled at the step router, not inside this subsection.
 */
export const PowerDistributionInformation: React.FC<
  PowerDistributionInformationProps
> = props => {
  const {
    sectionTitle = 'Power Distribution',
    attachmentsBySection,
    onAttachFiles,
    onDeleteAttachment,
    selectedFiles,
    onFilesSelected,
    offlineMode,
    onCaptionChange,
    pendingCaptionEdits,
  } = props;

  const showFullDetails =
    props.mode === 'initial'
      ? props.showFullDetails ?? false
      : props.showFullDetails ?? true;
  const requestStageForVisibility =
    props.mode === 'initial'
      ? String(
          (props.formData as { sysRequestStage?: string; stage?: string })
            .sysRequestStage ??
            (props.formData as { stage?: string }).stage ??
            '',
        )
      : String(props.request.sysRequestStage ?? props.request.stage ?? '');
  const requestIdForVisibility =
    props.mode === 'initial' ? props.requestId : props.request.id;
  const fieldVisibility = useFormFieldVisibilityContext({
    showFullDetails,
    requestStage: requestStageForVisibility,
    requestId: requestIdForVisibility,
  });

  if (props.mode === 'initial') {
    const { formData, updateFormData, requestId, disabled, isEditing } = props;
    const fd = formData as Record<string, unknown>;
    const initStr = (key: string): string => {
      const raw = fd[key];
      if (raw === undefined || raw === null) {
        return '';
      }
      return String(raw);
    };
    const requestStageStr = requestStageForVisibility;
    const distributionMetrics = subsectionHeaderMetricsProps(
      FACILITY_POWER_DISTRIBUTION_SUBSECTION_FIELDS,
      fd,
      fieldVisibility,
    );

    return (
      <SubsectionForm
        title={sectionTitle}
        {...distributionMetrics}
        titleSubtitle={FACILITY_POWER_DISTRIBUTION_SUBTITLE}
        sectionId={SECTION_IDS.FACILITY_POWER_DISTRIBUTION}
        requestId={requestId}
        allowAttachments={true}
        attachments={
          attachmentsBySection[SECTION_IDS.FACILITY_POWER_DISTRIBUTION] || []
        }
        onAttachFiles={() =>
          onAttachFiles?.(SECTION_IDS.FACILITY_POWER_DISTRIBUTION)
        }
        onDeleteAttachment={onDeleteAttachment}
        isEditing={isEditing || !disabled}
        disabled={disabled}
        selectedFiles={
          selectedFiles[SECTION_IDS.FACILITY_POWER_DISTRIBUTION] || []
        }
        onFilesSelected={
          onFilesSelected
            ? files =>
                onFilesSelected(SECTION_IDS.FACILITY_POWER_DISTRIBUTION, files)
            : undefined
        }
        isOffline={offlineMode}
      >
        <FormFieldStack>
          <PermissionAwareField
            fieldId="powerDistributionVoltageToLoad"
            requestStage={requestStageStr}
            requestId={requestId}
          >
            <NumberInput
              label="IT load voltage"
              value={initStr('powerDistributionVoltageToLoad')}
              onChangeText={value =>
                updateFormData('powerDistributionVoltageToLoad', value)
              }
              placeholder="Enter IT load voltage requirement"
              min={0}
              showStepper={false}
              disabled={disabled}
            />
          </PermissionAwareField>
          <PermissionAwareField
            fieldId="powerOverheadBuswayOrUnderFloor"
            requestStage={requestStageStr}
            requestId={requestId}
          >
            <FormDropdown
              label="Is a busway or under floor cabling to rack desired?"
              value={initStr('powerOverheadBuswayOrUnderFloor')}
              options={distributionMethodOptions}
              onSelect={value =>
                updateFormData('powerOverheadBuswayOrUnderFloor', String(value))
              }
              placeholder="Select distribution method"
              disabled={disabled}
            />
          </PermissionAwareField>
          <PermissionAwareField
            fieldId="powerBuswayAmperage"
            requestStage={requestStageStr}
            requestId={requestId}
          >
            <NumberInput
              label="Busway amperage"
              value={initStr('powerBuswayAmperage')}
              onChangeText={value =>
                updateFormData('powerBuswayAmperage', value)
              }
              placeholder="Enter busway amperage"
              min={0}
              showStepper={false}
              disabled={disabled}
            />
          </PermissionAwareField>
          <PermissionAwareField
            fieldId="powerBuswayLengthFt"
            requestStage={requestStageStr}
            requestId={requestId}
          >
            <UnitNumberField
              fieldKey="powerBuswayLengthFt"
              pair="lengthFt"
              label="Busway Length"
              value={initStr('powerBuswayLengthFt')}
              onChangeText={value =>
                updateFormData('powerBuswayLengthFt', value)
              }
              placeholder="Enter busway length in feet"
              min={0}
              showStepper={false}
              disabled={disabled}
              readOnlyMode={disabled}
            />
          </PermissionAwareField>
          <PermissionAwareField
            fieldId="powerUpsOutputVoltage"
            requestStage={requestStageStr}
            requestId={requestId}
          >
            <FormDropdown
              label="UPS output voltage"
              value={initStr('powerUpsOutputVoltage')}
              options={voltageOptions}
              onSelect={value =>
                updateFormData('powerUpsOutputVoltage', String(value))
              }
              placeholder="Select voltage"
              disabled={disabled}
            />
          </PermissionAwareField>
          <PermissionAwareField
            fieldId="powerTapOfBoxRequirements"
            requestStage={requestStageStr}
            requestId={requestId}
          >
            <FormInput
              label="Tap Off Box Requirements"
              value={initStr('powerTapOfBoxRequirements')}
              onChangeText={value =>
                updateFormData('powerTapOfBoxRequirements', value)
              }
              placeholder="Specify tap-off box requirements"
              multiline
              maxLength={10000}
              richText
              requestId={requestId}
              requestSection={SECTION_IDS.FACILITY_POWER_DISTRIBUTION}
              isOnline={!offlineMode}
              disabled={disabled}
            />
          </PermissionAwareField>
        </FormFieldStack>
      </SubsectionForm>
    );
  }

  const { request, isEditing, editedData, getValue, updateEditedData } = props;
  const { disabled: fieldDisabled, readOnlyMode } =
    requestStepFieldModeProps(isEditing);
  const permissionStage = String(
    request.stage ?? request.sysRequestStage ?? '',
  );
  const headerValues = {
    ...(request as unknown as Record<string, unknown>),
    ...(isEditing ? editedData ?? {} : {}),
  };
  const distributionMetrics = subsectionHeaderMetricsProps(
    FACILITY_POWER_DISTRIBUTION_SUBSECTION_FIELDS,
    headerValues,
    fieldVisibility,
  );

  if (!showFullDetails) {
    return null;
  }

  return (
    <SubsectionForm
      title={sectionTitle}
      {...distributionMetrics}
      titleSubtitle={FACILITY_POWER_DISTRIBUTION_SUBTITLE}
      sectionId={SECTION_IDS.FACILITY_POWER_DISTRIBUTION}
      requestId={request.id}
      allowAttachments={true}
      attachments={
        attachmentsBySection[SECTION_IDS.FACILITY_POWER_DISTRIBUTION] || []
      }
      onAttachFiles={() =>
        onAttachFiles?.(SECTION_IDS.FACILITY_POWER_DISTRIBUTION)
      }
      onDeleteAttachment={onDeleteAttachment}
      isEditing={isEditing}
      disabled={fieldDisabled}
      readOnlyMode={readOnlyMode}
      selectedFiles={
        selectedFiles[SECTION_IDS.FACILITY_POWER_DISTRIBUTION] || []
      }
      onFilesSelected={
        onFilesSelected
          ? files =>
              onFilesSelected(SECTION_IDS.FACILITY_POWER_DISTRIBUTION, files)
          : undefined
      }
      isOffline={offlineMode}
      onCaptionChange={onCaptionChange}
      pendingCaptionEdits={pendingCaptionEdits}
    >
      <FormFieldStack>
        <PermissionAwareField
          fieldId="powerDistributionVoltageToLoad"
          requestStage={permissionStage}
          requestId={request.id}
          showFullDetails={showFullDetails}
        >
          <NumberInput
            label="IT load voltage"
            value={getValue('powerDistributionVoltageToLoad')}
            onChangeText={
              isEditing
                ? value =>
                    updateEditedData('powerDistributionVoltageToLoad', value)
                : () => {}
            }
            placeholder="Enter IT load voltage requirement"
            min={0}
            showStepper={false}
            disabled={fieldDisabled}
            readOnlyMode={readOnlyMode}
          />
        </PermissionAwareField>
        <PermissionAwareField
          fieldId="powerOverheadBuswayOrUnderFloor"
          requestStage={permissionStage}
          requestId={request.id}
          showFullDetails={showFullDetails}
        >
          <FormDropdown
            label="Is a busway or under floor cabling to rack desired?"
            value={getValue('powerOverheadBuswayOrUnderFloor')}
            options={distributionMethodOptions}
            onSelect={
              isEditing
                ? value =>
                    updateEditedData(
                      'powerOverheadBuswayOrUnderFloor',
                      String(value),
                    )
                : () => {}
            }
            placeholder="Select distribution method"
            disabled={fieldDisabled}
            readOnlyMode={readOnlyMode}
          />
        </PermissionAwareField>
        <PermissionAwareField
          fieldId="powerBuswayAmperage"
          requestStage={permissionStage}
          requestId={request.id}
          showFullDetails={showFullDetails}
        >
          <NumberInput
            label="Busway amperage"
            value={getValue('powerBuswayAmperage')}
            onChangeText={
              isEditing
                ? value => updateEditedData('powerBuswayAmperage', value)
                : () => {}
            }
            placeholder="Enter busway amperage"
            min={0}
            showStepper={false}
            disabled={fieldDisabled}
            readOnlyMode={readOnlyMode}
          />
        </PermissionAwareField>
        <PermissionAwareField
          fieldId="powerBuswayLengthFt"
          requestStage={permissionStage}
          requestId={request.id}
          showFullDetails={showFullDetails}
        >
          <UnitNumberField
              fieldKey="powerBuswayLengthFt"
            pair="lengthFt"
            label="Busway Length"
            value={getValue('powerBuswayLengthFt')}
            onChangeText={
              isEditing
                ? value => updateEditedData('powerBuswayLengthFt', value)
                : () => {}
            }
            placeholder="Enter busway length in feet"
            min={0}
            showStepper={false}
            disabled={fieldDisabled}
            readOnlyMode={readOnlyMode}
          />
        </PermissionAwareField>
        <PermissionAwareField
          fieldId="powerUpsOutputVoltage"
          requestStage={permissionStage}
          requestId={request.id}
          showFullDetails={showFullDetails}
        >
          <FormDropdown
            label="UPS output voltage"
            value={String(getValue('powerUpsOutputVoltage') ?? '')}
            options={voltageOptions}
            onSelect={
              isEditing
                ? value =>
                    updateEditedData('powerUpsOutputVoltage', String(value))
                : () => {}
            }
            placeholder="Select voltage"
            disabled={fieldDisabled}
            readOnlyMode={readOnlyMode}
          />
        </PermissionAwareField>
        <PermissionAwareField
          fieldId="powerTapOfBoxRequirements"
          requestStage={permissionStage}
          requestId={request.id}
          showFullDetails={showFullDetails}
        >
          <FormInput
            label="Tap Off Box Requirements"
            value={getValue('powerTapOfBoxRequirements')}
            onChangeText={
              isEditing
                ? value => updateEditedData('powerTapOfBoxRequirements', value)
                : () => {}
            }
            placeholder="Specify tap-off box requirements"
            multiline
            maxLength={10000}
            richText
            requestId={String(request.id)}
            requestSection={SECTION_IDS.FACILITY_POWER_DISTRIBUTION}
            isOnline={!offlineMode}
            disabled={fieldDisabled}
            readOnlyMode={readOnlyMode}
          />
        </PermissionAwareField>
      </FormFieldStack>
    </SubsectionForm>
  );
};

PowerDistributionInformation.displayName = 'PowerDistributionInformation';
